// Datepicker
// ==========

import DateFormatter from '../Library/DateFormatter';
import Dates from '../Library/Dates';
import datepickerHU from '../Locale/DatepickerHU';
import DOM from '../Library/DOM';

const DEFAULT_SETTINGS = {
    className: 'datepicker',
    dateFormat: 'yyyy-MM-dd',
    dayNames: 'daysShort',
    locale: datepickerHU,
    selectedDate: undefined,
    titleFormat: 'MMMM yyyy',
    invalidDays: [],
};

class Datepicker extends EventTarget {
    constructor(settings) {
        super();
        this.settings = {};
        this.inputSettings = (settings.input) ? DOM.getElement(settings.input).dataset : {};
        const mergedSettings = { ...DEFAULT_SETTINGS, ...this.inputSettings, ...settings };
        this.formatter = new DateFormatter(mergedSettings.locale);
        this.update(mergedSettings);
    }

    createDatepicker() {
        this.$dayNames = this.createElement('daynames');
        this.$days = this.createElement('days');

        const header = this.createElement('header');
        this.$title = this.createElement('title');
        this.$navNext = this.createStepper('nav next', 1);
        this.$navPrev = this.createStepper('nav prev', -1);
        header.append(this.$navPrev, this.$title, this.$navNext);

        const body = this.createElement('body');
        body.append(this.$dayNames, this.$days);

        this.$datepicker = this.createElement();
        this.$datepicker.className = this.settings.className;
        this.$datepicker.append(header, body);
    }

    createDay(className, day, month, year) {
        const dayElement = this.createElement(`day ${className}`);
        if (day !== undefined) {
            const date = new Date(year, month, day);
            dayElement.dataset.date = Dates.getDateString(date);
            dayElement.textContent = date.getDate();
            dayElement.addEventListener('click', (e) => { this.onDayClick(e); });
        }
        this.$days.append(dayElement);
    }

    createDays(year, month) {
        this.year = year + Math.floor(month / 12);
        this.month = month % 12;
        if (this.month < 0) this.month += 12;
        const startDay = new Date(this.year, this.month);
        const daysPreviousMonth = Dates.getDay(startDay, this.settings.locale.firstDay);
        const daysCurrentMonth = new Date(this.year, this.month + 1, 0).getDate();

        this.$days.replaceChildren();
        for (let i = -daysPreviousMonth + 1; i <= 0; i += 1) {
            if (this.settings.showExternalDays) {
                this.createDay('external', i, this.month, this.year);
            } else {
                this.createDay('empty');
            }
        }
        for (let i = 1; i <= daysCurrentMonth; i += 1) {
            this.createDay('', i, this.month, this.year);
        }
        if (this.settings.showExternalDays) {
            const end = 42 - daysPreviousMonth;
            for (let i = daysCurrentMonth; i < end; i += 1) {
                this.createDay('external', i + 1, this.month, this.year);
            }
        }
    }

    createElement(classPostfix, textContent, tagName = 'div') {
        const e = document.createElement(tagName);
        if (classPostfix) e.className = `${this.settings.className}-${classPostfix}`;
        if (textContent) e.textContent = textContent;
        return e;
    }

    createStepper(classPostfix, value) {
        const stepper = this.createElement(classPostfix);
        stepper.addEventListener('click', () => { this.stepMonth(value); });
        return stepper;
    }

    deselect() {
        this.update({ selectedDate: undefined });
    }

    formatDate(date, format) {
        return (date) ? this.formatter.format(date, format || this.settings.dateFormat) : '';
    }

    onDayClick(e) {
        if (!e.currentTarget.classList.contains('disabled') || e.currentTarget.classList.contains('selected')) {
            if (e.currentTarget.dataset.date === Dates.getDateString(this.selectedDate)) {
                this.deselect();
            } else {
                this.selectDate(e.currentTarget.dataset.date);
            }
            this.dispatchEvent(new Event('select'));
        }
    }

    selectDate(date) {
        this.update({ selectedDate: date });
    }

    stepMonth(value) {
        this.createDays(this.year, this.month + value);
        this.updateDays();
        this.updateNav();
    }

    update(settings) {
        const ps = { ...this.settings };
        this.settings = { ...this.settings, ...settings };
        const ns = this.settings;

        // Input
        if (ns.input) {
            this.$input = DOM.getElement(ns.input);
            this.inputProperty = (this.$input.tagName === 'INPUT') ? 'value' : 'textContent';
        }

        // Datepicker element
        const newDp = ns.className !== ps.className;
        if (newDp) this.createDatepicker();

        // Custom CSS classes
        if (newDp || ns.classes !== ps.classes) {
            if (!newDp) this.$datepicker.className = ns.className;
            if (ns.classes) this.$datepicker.classList.add(...ns.classes);
        }

        // Days of the week
        if (newDp || ns.locale !== ps.locale || ns.dayNames !== ps.dayNames) {
            this.$dayNames.replaceChildren();
            for (let i = 0; i < 7; i += 1) {
                const d = Dates.getDayIndex(i, this.settings.locale.firstDay);
                this.$dayNames.append(this.createElement('dayname', ns.locale[ns.dayNames][d]));
            }
        }

        // Selected Date
        if ('selectedDate' in settings) {
            const newDate = Dates.getDate(ns.selectedDate);
            if (this.selectedDate !== newDate) {
                this.selectedDate = newDate;
                this.settings.selectedDate = newDate;
                this.updateInput();
                this.dispatchEvent(new Event('change'));
            }
        } else if (ns.dateFormat !== ps.dateFormat) {
            this.updateInput();
        }

        // Days
        let startDate = new Date(this.selectedDate || this.settings.startDate || Date.now());
        this.settings.minDate = Dates.getDate(ns.minDate);
        if (startDate < ns.minDate) startDate = ns.minDate;
        this.settings.maxDate = Dates.getDate(ns.maxDate);
        if (startDate > ns.maxDate) startDate = ns.maxDate;
        const year = startDate.getFullYear();
        const month = startDate.getMonth();
        const newDays = this.month !== month || this.year !== year || newDp;
        if (newDays) this.createDays(year, month);
        this.updateDays();

        // Nav
        if (newDays || ns.locale !== ps.locale || ns.titleFormat !== ps.titleFormat) {
            this.updateNav();
        }

        // Append
        this.$container = DOM.getElement(ns.container);
        if (this.$container && ns.container !== ps.container) {
            this.$coontainer.append(this.$datepicker);
        } else if (this.$input && ns.input !== ps.input) {
            this.$input.after(this.$datepicker);
        }
    }

    updateDays() {
        const today = Dates.getDateString(new Date());
        const minDate = Dates.getDateString(this.settings.minDate);
        const maxDate = Dates.getDateString(this.settings.maxDate);
        const selectedDate = Dates.getDateString(this.selectedDate);
        this.$days.querySelectorAll('[data-date]').forEach((element) => {
            const { date } = element.dataset;
            const disabled = date < minDate || (maxDate && date > maxDate);
            const invalidDayDisabled = this.settings.invalidDays ? this.settings.invalidDays.some((dayOfTheWeek) => dayOfTheWeek === new Date(date).getDay()) : false;
            element.classList.toggle('current', date === today);
            element.classList.toggle('disabled', !!disabled || invalidDayDisabled);
            element.classList.toggle('selected', date === selectedDate);
        });
    }

    updateInput() {
        if (this.$input) {
            this.$input[this.inputProperty] = this.formatDate(this.selectedDate);
        }
    }

    updateNav() {
        this.$title.textContent = this.formatter.format(
            new Date(this.year, this.month),
            this.settings.titleFormat,
        );

        let hidden = false;
        if (this.settings.minDate) {
            const minDate = new Date(this.settings.minDate);
            const minYear = minDate.getFullYear();
            hidden = this.year < minYear || (this.year === minYear && this.month <= minDate.getMonth());
        }
        this.$navPrev.classList.toggle('hidden', hidden);

        hidden = false;
        if (this.settings.maxDate) {
            const maxDate = new Date(this.settings.maxDate);
            const maxYear = maxDate.getFullYear();
            hidden = this.year > maxYear || (this.year === maxYear && this.month >= maxDate.getMonth());
        }
        this.$navNext.classList.toggle('hidden', hidden);
    }
}

export default Datepicker;
